import React, { useState, useEffect, useMemo } from 'react';
import {
  TextField,
  Button,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Menu,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { toast } from 'react-toastify';
import './ReservationRateCardForm.scss';

const ReservationRateCardForm = ({
  dutyTypeName,
  mode,
  existingRateCard,

  existingRateCards = [],
  isMultiDay = false,
  finalConfig,
  setFinalConfig,
  onEditStart,
  alwaysEditable = false,
}) => {
  // Shown under every "update" button: an edit here travels with this booking
  // only; the corporate's stored rate card is never changed from this page.
  const bookingOnlyNote = (
    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
      Applies to this booking only. The corporate&apos;s rate card is not changed.
    </Typography>
  );
  const isAirport = dutyTypeName === 'Airport Transfer';
  const isOutstation = dutyTypeName === 'Outstation';
  const isRental = dutyTypeName === 'Rental';
  const isEditing = mode !== 'defaultRateView';

  const emptyPackageConfig = () => ({
    name: isOutstation ? '' : 'Package-1',
    hours: isOutstation ? 1 : '',
    durationType: isOutstation ? 'Day' : 'Hour',
    km: '',
    price: '',
    graceHour: '',
    graceType: 'Hour',
    extraPriceHour: '',
    graceKm: '',
    extraPriceKm: '',
    advanceBookingCharge: '',
    nightAllowance: '',
    earlyStartAllowance: '',
    driverAllowance: '',
  });

  const emptyBasicConfig = () => ({
    baseRate: '',
    surCharge: '',
    platformCharge: '',
  });

  // ---------- AIRPORT: Basic Configuration state ----------
  const [airportConfig, setAirportConfig] = useState({
    rateType: 'perKm',
    baseRate: '',
    flatAmount: '',
    minKm: '',
    surCharge: '',
    platformCharge: '',
    currentRate: '',
    nightAllowance: '',
    earlyStartAllowance: '',
  });

  const [initialAirportConfig, setInitialAirportConfig] = useState(null);

  // ---------- OUTSTATION/RENTAL: basic + package state ----------
  const [basicConfig, setBasicConfig] = useState(emptyBasicConfig());
  const [packageConfig, setPackageConfig] = useState(emptyPackageConfig());

  const [initialPackageState, setInitialPackageState] = useState(null);

  const [packages, setPackages] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);
  const [isEditingPackage, setIsEditingPackage] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  const [initialEditSnapshot, setInitialEditSnapshot] = useState(null);
  const [activePackageIndex, setActivePackageIndex] = useState(null);

  const disabled =
    !isOutstation &&
    !isAirport &&
    (mode === 'defaultRateView' || mode === 'editedRate') &&
    !isEditingPackage &&
    !alwaysEditable;

  // Mandatory rate fields (2026-09-25): anything that sets the base fare or
  // the size of an allowance. Matches the booking service's shape guard
  // (helpers/rateConfigShape). Purely additive lines stay optional.
  const filled = (v) => String(v ?? '').trim() !== '';
  const isPackageValid =
    filled(packageConfig.hours) &&
    filled(packageConfig.km) &&
    filled(packageConfig.price) &&
    filled(packageConfig.graceKm) &&
    filled(packageConfig.extraPriceKm) &&
    filled(packageConfig.graceHour) &&
    // Outstation never charges extra hours as hours; the rate is optional there.
    (isOutstation || filled(packageConfig.extraPriceHour));

  const isPackageChangedFromEdit =
    isEditingPackage && initialEditSnapshot
      ? JSON.stringify({ ...basicConfig, ...packageConfig }) !== JSON.stringify(initialEditSnapshot)
      : true;

  const isRentalButtonDisabled = !isPackageValid || !isPackageChangedFromEdit;

  const isOutstationChanged =
    initialPackageState !== null &&
    (JSON.stringify(basicConfig) !== JSON.stringify(initialPackageState.basicConfig) ||
      JSON.stringify(packageConfig) !== JSON.stringify(initialPackageState.packageConfig));

  const isOutstationButtonDisabled = initialPackageState
    ? !(isPackageValid && isOutstationChanged)
    : !isPackageValid;

  // Airport: required fields depend on the rate type. perKm: base rate and
  // per-km rate. flatRate: flat amount. flatPlusKm: flat amount, included km
  // and per-km rate.
  const isAirportValid =
    airportConfig.rateType === 'perKm'
      ? filled(airportConfig.baseRate) && filled(airportConfig.currentRate)
      : airportConfig.rateType === 'flatPlusKm'
        ? filled(airportConfig.flatAmount) &&
          filled(airportConfig.minKm) &&
          filled(airportConfig.currentRate)
        : filled(airportConfig.flatAmount);

  const isAirportChanged =
    initialAirportConfig !== null &&
    JSON.stringify(airportConfig) !== JSON.stringify(initialAirportConfig);

  // An edited card must be both changed and complete.
  const isAirportButtonDisabled = initialAirportConfig
    ? !(isAirportChanged && isAirportValid)
    : !isAirportValid;

  const [isAddPackageModalOpen, setIsAddPackageModalOpen] = useState(false);
  const [newPackageName, setNewPackageName] = useState('');
  const [newPackageDescription, setNewPackageDescription] = useState('');
  const [isAddAttempted, setIsAddAttempted] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRowIndex, setMenuRowIndex] = useState(null);

  const mapRateCardToPackageRow = (rateCard) => {
    const rc = rateCard?.rateConfig || {};
    return {
      id: rateCard?.id,
      name: rateCard?.rateCardType?.name || rc?.name || 'Package-1',
      hours: rc?.duration || '',
      durationType: (rc?.durationType || 'hour') === 'day' ? 'Day' : 'Hour',
      km: rc?.maxDistanceKms || '',
      price: rc?.cost || '',
      graceHour: rc?.extraHrs?.graceValue || '',
      graceType: (rc?.extraHrs?.unitType || 'Hour') === 'Min' ? 'Min' : 'Hour',
      extraPriceHour: rc?.extraHrs?.rate || '',
      graceKm: rc?.extraKM?.graceValue || '',
      extraPriceKm: rc?.extraKM?.rate || '',
      advanceBookingCharge: rc?.additionalCharges?.[0]?.rate || '',
      nightAllowance: rc?.nightAllowance?.rate || '',
      earlyStartAllowance: rc?.earlyStartAllowance?.rate || '',
      driverAllowance: rc?.driverAllowance?.rate || '',
      baseRate: rateCard?.baseRate || '',
      surCharge: rateCard?.surCharge || '',
      platformCharge: rateCard?.platformCharge || '',
    };
  };

  const existingRateCardsKey = useMemo(
    () => (Array.isArray(existingRateCards) ? existingRateCards.map((rc) => rc?.id).join(',') : ''),
    [existingRateCards],
  );

  // ---------- Prefill from existing rate card (defaultRateView / editedRate) ----------
  useEffect(() => {
    if (isAirport) {
      if (!existingRateCard) return;
      const rc = existingRateCard?.rateConfig || {};
      // The card's airport pricing type lives in rateConfig.pricingType
      // (perKm / flatRate / flatPlusKm); the row's `rateType` column is the
      // card family and is always 'flat_per_km' for airport. Prefilling a
      // constant 'perKm' here showed a flat card as "/km" with a blank /KM box.
      const cardPricingType = ['perKm', 'flatRate', 'flatPlusKm'].includes(rc?.pricingType)
        ? rc.pricingType
        : 'perKm';
      const prefill = {
        rateType: cardPricingType,
        baseRate: existingRateCard?.baseRate || '',
        flatAmount: rc?.flatAmount ?? '',
        minKm: rc?.minKm ?? '',
        surCharge: existingRateCard?.surCharge || '',
        platformCharge: existingRateCard?.platformCharge || '',
        currentRate: rc?.base_rate_per_km ?? '',
        nightAllowance:
          rc?.nightAllowance?.rate ??
          existingRateCard?.nightAllowance?.rate ??
          existingRateCard?.nightAllowance ??
          '',
        earlyStartAllowance:
          rc?.earlyStartAllowance?.rate ??
          existingRateCard?.earlyStartAllowance?.rate ??
          existingRateCard?.earlyStartAllowance ??
          '',
      };
      setAirportConfig(prefill);
      setInitialAirportConfig(prefill);
    } else if (isOutstation) {
      if (!existingRateCard) return;
      const rc = existingRateCard?.rateConfig || {};
      const basicPrefill = {
        baseRate: existingRateCard?.baseRate || '',
        surCharge: existingRateCard?.surCharge || '',
        platformCharge: existingRateCard?.platformCharge || '',
      };
      const packagePrefill = {
        ...emptyPackageConfig(),
        name: existingRateCard?.rateCardType?.name || 'Outstation Rate',
        hours: rc?.duration || 1,
        durationType: (rc?.durationType || 'day') === 'hour' ? 'Hour' : 'Day',
        km: rc?.maxDistanceKms || '',
        price: rc?.cost || '',
        graceHour: rc?.extraHrs?.graceValue || '',
        graceType: (rc?.extraHrs?.unitType || 'Hour') === 'Min' ? 'Min' : 'Hour',
        extraPriceHour: rc?.extraHrs?.rate || '',
        graceKm: rc?.extraKM?.graceValue || '',
        extraPriceKm: rc?.extraKM?.rate || '',
        advanceBookingCharge: rc?.additionalCharges?.[0]?.rate || '',
        nightAllowance: rc?.nightAllowance?.rate || '',
        earlyStartAllowance: rc?.earlyStartAllowance?.rate || '',
        driverAllowance: rc?.driverAllowance?.rate || '',
      };
      setBasicConfig(basicPrefill);
      setPackageConfig(packagePrefill);
      setInitialPackageState({
        basicConfig: basicPrefill,
        packageConfig: packagePrefill,
      });
    } else {
      if (Array.isArray(existingRateCards) && existingRateCards.length > 0) {
        const rows = existingRateCards.map(mapRateCardToPackageRow);
        setPackages(rows);

        setActivePackageIndex(rows.length === 1 ? 0 : null);
      } else if (existingRateCard) {
        setPackages([mapRateCardToPackageRow(existingRateCard)]);
        setActivePackageIndex(0);
      }
    }
  }, [existingRateCard?.id, existingRateCardsKey]);

  useEffect(() => {
    if (isAirport) {
      setFinalConfig({
        isAirport: true,
        name: 'Airport Rate',
        baseRate: airportConfig.rateType === 'perKm' ? airportConfig.baseRate : '',
        flatAmount: airportConfig.flatAmount,
        minKm: airportConfig.minKm,
        surCharge: airportConfig.surCharge,
        platformCharge: airportConfig.platformCharge,
        rateType: airportConfig.rateType,
        rateConfig: {
          cost:
            airportConfig.rateType === 'perKm' ? airportConfig.baseRate : airportConfig.flatAmount,
          currentRate: airportConfig.currentRate,
          kmCharge: airportConfig.kmCharge,
          minKm: airportConfig.minKm,
          nightAllowance: { rate: airportConfig.nightAllowance },
          earlyStartAllowance: { rate: airportConfig.earlyStartAllowance },
        },
      });
      return;
    }

    // Rental — use the last activated package
    const activePkg = activePackageIndex !== null ? packages[activePackageIndex] : null;
    if (activePkg) {
      setFinalConfig({
        isAirport: false,
        name: activePkg.name,
        baseRate: activePkg.baseRate,
        surCharge: activePkg.surCharge,
        platformCharge: activePkg.platformCharge,
        rateConfig: {
          name: activePkg.name,
          duration: activePkg.hours,
          durationType: activePkg.durationType?.toLowerCase() || 'hour',
          maxDistanceKms: activePkg.km,
          cost: activePkg.price,
          extraKM: {
            rate: activePkg.extraPriceKm,
            graceValue: activePkg.graceKm,
          },
          extraHrs: {
            rate: activePkg.extraPriceHour,
            graceValue: activePkg.graceHour,
            unitType: activePkg.graceType,
          },
          nightAllowance: { rate: activePkg.nightAllowance },
          earlyStartAllowance: { rate: activePkg.earlyStartAllowance },
          driverAllowance: { rate: activePkg.driverAllowance },
          additionalCharges: [
            {
              name: 'Advance Booking',
              label: 'Advance Booking',
              unitType: 'Rs',
              rate: activePkg.advanceBookingCharge || 0,
            },
          ],
        },
      });
      return;
    }

    if (isRental && packages.length > 1 && activePackageIndex === null) {
      setFinalConfig(null);
      return;
    }

    if (isOutstation && packages.length === 0) {
      const hasData = basicConfig.baseRate || packageConfig.km || packageConfig.price;
      if (hasData) {
        setFinalConfig({
          isAirport: false,
          name: packageConfig.name || 'Outstation Rate',
          baseRate: basicConfig.baseRate,
          surCharge: basicConfig.surCharge,
          platformCharge: basicConfig.platformCharge,
          rateConfig: {
            name: packageConfig.name || 'Outstation Rate',
            duration: packageConfig.hours,
            durationType: packageConfig.durationType?.toLowerCase() || 'day',
            maxDistanceKms: packageConfig.km,
            cost: packageConfig.price,
            extraKM: {
              rate: packageConfig.extraPriceKm,
              graceValue: packageConfig.graceKm,
            },
            extraHrs: {
              rate: packageConfig.extraPriceHour,
              graceValue: packageConfig.graceHour,
              unitType: packageConfig.graceType,
            },
            nightAllowance: { rate: packageConfig.nightAllowance },
            earlyStartAllowance: { rate: packageConfig.earlyStartAllowance },
            driverAllowance: { rate: packageConfig.driverAllowance },
            additionalCharges: [
              {
                name: 'Advance Booking',
                label: 'Advance Booking',
                unitType: 'Rs',
                rate: packageConfig.advanceBookingCharge || 0,
              },
            ],
          },
        });
      }
    }
  }, [airportConfig, packages, packageConfig, basicConfig, activePackageIndex]);

  const handleAirportChange = (field, value) => {
    setAirportConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleBasicChange = (field, value) => {
    setBasicConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handlePackageChange = (field, value) => {
    setPackageConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateAirportConfig = () => {
    if (isAirportButtonDisabled) return;
    if (mode !== 'customRate' && onEditStart) onEditStart();
    setInitialAirportConfig(airportConfig);
    toast.success('Airport rate configuration updated');
  };

  const handleActivatePackage = () => {
    if (!isPackageValid) return;
    // Departing from the card happens HERE — a package saved or a new one
    // activated — and the button is disabled unless something changed.
    if (mode !== 'customRate' && onEditStart) onEditStart();
    const newPkg = { ...basicConfig, ...packageConfig };
    if (isEditingPackage && editIndex !== null) {
      setPackages((prev) => prev.map((p, i) => (i === editIndex ? newPkg : p)));

      setActivePackageIndex(editIndex);
      setIsEditingPackage(false);
      setEditIndex(null);
      setInitialEditSnapshot(null);
      toast.success('Package updated');
    } else {
      const nextIndex = packages.length;
      setPackages((prev) => [...prev, newPkg]);
      setActivePackageIndex(nextIndex);
      toast.success('Package activated');
    }
    if (!isOutstation) {
      setPackageConfig(emptyPackageConfig());
      setBasicConfig(emptyBasicConfig());
    }
  };

  const handleUpdateOutstationRate = () => {
    if (isOutstationButtonDisabled) return;
    if (mode !== 'customRate' && onEditStart) onEditStart();
    setInitialPackageState({ basicConfig, packageConfig });
    toast.success('Outstation rate updated');
  };

  const handleEditRow = (index) => {
    const row = packages[index];
    setBasicConfig({
      baseRate: row.baseRate,
      surCharge: row.surCharge,
      platformCharge: row.platformCharge,
    });
    setPackageConfig({ ...row });
    setIsEditingPackage(true);
    setEditIndex(index);
    setMenuAnchor(null);

    setInitialEditSnapshot({ ...row });
    // Opening the editor is not a departure from the card; saving a change is
    // (handleActivatePackage). Cancelling leaves the booking on its card.
  };

  const handleDeleteRow = (index) => {
    setPackages((prev) => prev.filter((_, i) => i !== index));
    setMenuAnchor(null);
  };

  const handleAddNewPackageName = () => {
    setIsAddAttempted(true);
    if (!newPackageName.trim()) return;
    setPackageOptions((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: newPackageName,
        description: newPackageDescription,
      },
    ]);
    setPackageConfig((prev) => ({ ...prev, name: newPackageName }));
    setNewPackageName('');
    setNewPackageDescription('');
    setIsAddPackageModalOpen(false);
    setIsAddAttempted(false);
  };

  const numField = (label, value, onChangeFn, extraProps = {}) => (
    <TextField
      size="small"
      type="number"
      label={label}
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChangeFn(e.target.value)}
      onWheel={(e) => e.target.blur()}
      fullWidth
      {...extraProps}
    />
  );

  const disabledBtnSx = {
    '&.Mui-disabled': {
      backgroundColor: '#B0BEC5',
      color: '#ffffff',
    },
  };

  // ================= AIRPORT TRANSFER =================
  if (isAirport) {
    return (
      <Box className="reservation-rate-card-form rateCardSlotWrapper br10 commonBorder p20 mb20 basic-config-section">
        <Typography variant="body1" className="fw600 mb10">
          Basic Configuration
        </Typography>

        <div className="additional-package-grid" style={{ marginBottom: '16px' }}>
          <TextField
            size="small"
            select
            SelectProps={{ native: true }}
            label="Rate Type"
            value={airportConfig.rateType}
            disabled={disabled}
            onChange={(e) => handleAirportChange('rateType', e.target.value)}
          >
            <option value="perKm">/km</option>
            <option value="flatRate">Flat Rate</option>
            <option value="flatPlusKm">Flat Rate + /km</option>
          </TextField>

          {airportConfig.rateType === 'perKm' &&
            numField('Base Rate *', airportConfig.baseRate, (v) =>
              handleAirportChange('baseRate', v),
            )}

          {(airportConfig.rateType === 'flatRate' || airportConfig.rateType === 'flatPlusKm') &&
            numField('Flat Rate *', airportConfig.flatAmount, (v) =>
              handleAirportChange('flatAmount', v),
            )}

          {airportConfig.rateType === 'flatPlusKm' &&
            numField('Included KM *', airportConfig.minKm, (v) => handleAirportChange('minKm', v))}

          {numField('Surcharge', airportConfig.surCharge, (v) =>
            handleAirportChange('surCharge', v),
          )}

          {numField('Platform Charge', airportConfig.platformCharge, (v) =>
            handleAirportChange('platformCharge', v),
          )}

          {(airportConfig.rateType === 'perKm' || airportConfig.rateType === 'flatPlusKm') &&
            numField('/KM *', airportConfig.currentRate, (v) =>
              handleAirportChange('currentRate', v),
            )}

          {numField('Night Allowance', airportConfig.nightAllowance, (v) =>
            handleAirportChange('nightAllowance', v),
          )}

          {numField('Early Start Allowance', airportConfig.earlyStartAllowance, (v) =>
            handleAirportChange('earlyStartAllowance', v),
          )}
        </div>

        <Box sx={{ mt: 2 }}>
          <Button
            type="button"
            variant="contained"
            onClick={handleUpdateAirportConfig}
            disabled={isAirportButtonDisabled}
            sx={{
              backgroundColor: 'var(--brand-primary)',
              borderRadius: '8px',
              paddingY: '8px',
              paddingX: '24px',
              textTransform: 'none',
              fontWeight: 500,
              color: 'white',
              '&:hover': { backgroundColor: 'var(--brand-primary)' },
              ...disabledBtnSx,
            }}
          >
            Update Configuration
          </Button>
          {bookingOnlyNote}
        </Box>
      </Box>
    );
  }

  const packagesTable =
    isRental && packages.length > 0 ? (
      <div className="mt-4" style={{ overflowX: 'auto', marginBottom: '16px' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
            border: '1px solid rgb(232 232 232 / var(--tw-border-opacity, 1))',
          }}
        >
          <thead>
            <tr>
              {[
                'Pack',
                'Duration',
                'Km',
                'Price',
                'Grace Km',
                'Extra/Km',
                'Grace Hr',
                'Extra/Hr',
                'Adv Charge',
                ...(isOutstation ? ['Night Allowance', 'Early Start', 'Driver Allowance'] : []),
                packages.length > 0 ? ' ' : null,
              ]
                .filter(Boolean)
                .map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: '8px',
                      backgroundColor: 'var(--brand-primary)',
                      color: '#ffffff',
                    }}
                  >
                    {h}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {packages.map((row, idx) => (
              <tr key={row.id ?? idx}>
                <td style={{ padding: '8px' }}>{isOutstation ? '1 Day' : row.name}</td>
                <td style={{ padding: '8px' }}>
                  {row.hours} {row.durationType}
                </td>
                <td style={{ padding: '8px' }}>{row.km || 0}km</td>
                <td style={{ padding: '8px' }}>{row.price || 0}</td>
                <td style={{ padding: '8px' }}>{row.graceKm || 0}</td>
                <td style={{ padding: '8px' }}>{row.extraPriceKm || 0}</td>
                <td style={{ padding: '8px' }}>{row.graceHour || 0}</td>
                <td style={{ padding: '8px' }}>{row.extraPriceHour || 0}</td>
                <td style={{ padding: '8px' }}>{row.advanceBookingCharge || 0}</td>
                {isOutstation && (
                  <>
                    <td style={{ padding: '8px' }}>{row.nightAllowance || 0}</td>
                    <td style={{ padding: '8px' }}>{row.earlyStartAllowance || 0}</td>
                    <td style={{ padding: '8px' }}>{row.driverAllowance || 0}</td>
                  </>
                )}
                {packages.length > 0 && (
                  <td style={{ padding: '8px' }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setMenuAnchor(e.currentTarget);
                        setMenuRowIndex(idx);
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          PaperProps={{
            sx: {
              minWidth: 'unset',
              width: 'auto',
            },
          }}
          MenuListProps={{
            sx: {
              paddingY: '4px',
            },
          }}
        >
          <MenuItem
            onClick={() => handleEditRow(menuRowIndex)}
            sx={{
              fontSize: '13px',
              minHeight: 'unset',
              paddingY: '6px',
              paddingX: '16px',
            }}
          >
            Select
          </MenuItem>
        </Menu>
      </div>
    ) : null;

  // ================= OUTSTATION / RENTAL =================
  return (
    <div className="reservation-rate-card-form rateCardSlotWrapper">
      {packagesTable}
      <div
        className="bg-white border border-gray-200 rounded-lg p-4"
        style={{ marginBottom: '16px' }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'center',
          }}
        >
          {isRental && (
            <TextField
              size="small"
              select
              SelectProps={{ native: true }}
              label={isMultiDay ? 'Multi-day package' : 'Select Package'}
              // A corporate has at most one multi-day card, so there is nothing
              // to choose between; the backend uses that card regardless.
              disabled={disabled || (isMultiDay && packages.length <= 1)}
              helperText={
                isMultiDay
                  ? packages.length > 0
                    ? 'Per-day package used for every day of a multi-day booking.'
                    : 'No multi-day card for this corporate: enter the per-day package below.'
                  : undefined
              }
              value={packageConfig.name}
              onChange={(e) => handlePackageChange('name', e.target.value)}
              style={{ flex: '1', minWidth: '180px' }}
            >
              {packageOptions.length === 0 && (
                <option value={packageConfig.name}>{packageConfig.name}</option>
              )}
              {packageOptions.map((opt) => (
                <option key={opt.id} value={opt.name}>
                  {opt.name}
                </option>
              ))}
            </TextField>
          )}

          {numField('Base Fare', basicConfig.baseRate, (v) => handleBasicChange('baseRate', v), {
            style: { flex: '1', minWidth: '160px' },
          })}
          {numField('Surcharge', basicConfig.surCharge, (v) => handleBasicChange('surCharge', v), {
            style: { flex: '1', minWidth: '160px' },
          })}
          {numField(
            'Platform Charge',
            basicConfig.platformCharge,
            (v) => handleBasicChange('platformCharge', v),
            { style: { flex: '1', minWidth: '160px' } },
          )}
        </div>

        {isRental && !disabled && !(isMultiDay && packages.length > 0) && (
          <Button
            variant="text"
            sx={{
              color: '#2563eb',
              textTransform: 'none',
              fontSize: '0.875rem',
              padding: 0,
              mt: 1,
            }}
            onClick={() => setIsAddPackageModalOpen(true)}
          >
            Add new package
          </Button>
        )}
      </div>

      {/* Package configuration card */}
      <Box className="br10 commonBorder p20 mb20">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'center',
            width: '100%',
            marginBottom: '16px',
          }}
        >
          <Typography variant="body1" className="fw600">
            {`${dutyTypeName} package configuration`}
          </Typography>
          {isRental ? (
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={packageConfig.durationType}
                onChange={(e) => handlePackageChange('durationType', e.target.value)}
              >
                <FormControlLabel
                  value="Hour"
                  control={<Radio size="small" disabled={disabled} />}
                  label="Hour"
                  style={{ marginRight: 0 }}
                />
              </RadioGroup>
            </FormControl>
          ) : (
            <span />
          )}
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          {isRental && (
            <TextField
              label="Package name"
              value={packageConfig.name}
              size="small"
              fullWidth
              required
              disabled={true}
              style={{ flex: '1', minWidth: '160px' }}
            />
          )}
          {numField(
            packageConfig.durationType === 'Day' ? 'Days *' : 'Hours *',
            packageConfig.hours,
            (v) => handlePackageChange('hours', v),
            {
              style: { flex: '1', minWidth: '120px' },
              disabled: disabled || isOutstation,
            },
          )}
          {numField('Max Distance (Km) *', packageConfig.km, (v) => handlePackageChange('km', v), {
            style: { flex: '1', minWidth: '120px' },
          })}
          {numField('Price *', packageConfig.price, (v) => handlePackageChange('price', v), {
            style: { flex: '1', minWidth: '120px' },
          })}
        </div>

        {/* Additional package configuration */}
        <Typography variant="body2" className="fw600 mb10">
          Additional package configuration
        </Typography>
        <div className="additional-package-grid" style={{ marginBottom: '24px' }}>
          <div className="border border-gray-200 rounded-lg p-4">
            {isRental && (
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={packageConfig.graceType}
                  onChange={(e) => handlePackageChange('graceType', e.target.value)}
                >
                  <FormControlLabel
                    value="Min"
                    control={<Radio size="small" disabled={disabled} />}
                    label="Minute"
                  />
                  <FormControlLabel
                    value="Hour"
                    control={<Radio size="small" disabled={disabled} />}
                    label="Hour"
                  />
                </RadioGroup>
              </FormControl>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              {numField(`Grace ${packageConfig.graceType} *`, packageConfig.graceHour, (v) =>
                handlePackageChange('graceHour', v),
              )}
              {numField(
                `Extra price/${packageConfig.graceType}${isOutstation ? '' : ' *'}`,
                packageConfig.extraPriceHour,
                (v) => handlePackageChange('extraPriceHour', v),
              )}
            </div>
          </div>

          <div
            className="border border-gray-200 rounded-lg p-4"
            style={{ display: 'flex', alignItems: 'flex-end' }}
          >
            <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
              {numField('Grace Km *', packageConfig.graceKm, (v) =>
                handlePackageChange('graceKm', v),
              )}
              {numField('Extra price/Km *', packageConfig.extraPriceKm, (v) =>
                handlePackageChange('extraPriceKm', v),
              )}
            </div>
          </div>

          <div
            className="border border-gray-200 rounded-lg p-4"
            style={{ display: 'flex', alignItems: 'flex-end' }}
          >
            {numField('Advance booking charge', packageConfig.advanceBookingCharge, (v) =>
              handlePackageChange('advanceBookingCharge', v),
            )}
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            {numField(
              isOutstation ? 'Night Allowance (per night)' : 'Night Allowance',
              packageConfig.nightAllowance,
              (v) => handlePackageChange('nightAllowance', v),
            )}
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            {numField('Early Start Allowance', packageConfig.earlyStartAllowance, (v) =>
              handlePackageChange('earlyStartAllowance', v),
            )}
          </div>

          {isOutstation && (
            <div className="border border-gray-200 rounded-lg p-4">
              {numField('Driver Allowance (per day)', packageConfig.driverAllowance, (v) =>
                handlePackageChange('driverAllowance', v),
              )}
            </div>
          )}
        </div>

        {isRental && !disabled && (
          <Button
            type="button"
            variant="contained"
            onClick={handleActivatePackage}
            disabled={isRentalButtonDisabled}
            sx={{
              backgroundColor: 'var(--brand-primary)',
              borderRadius: '8px',
              paddingY: '8px',
              paddingX: '24px',
              textTransform: 'none',
              fontWeight: 500,
              color: 'white',
              '&:hover': { backgroundColor: 'var(--brand-primary)' },
              ...disabledBtnSx,
            }}
          >
            {isEditingPackage ? 'Update Package' : 'Activate Package'}
          </Button>
        )}
        {isRental && bookingOnlyNote}

        {isOutstation && (
          <Button
            type="button"
            variant="contained"
            onClick={handleUpdateOutstationRate}
            disabled={isOutstationButtonDisabled}
            sx={{
              backgroundColor: 'var(--brand-primary)',
              borderRadius: '8px',
              paddingY: '8px',
              paddingX: '24px',
              textTransform: 'none',
              fontWeight: 500,
              color: 'white',
              '&:hover': { backgroundColor: 'var(--brand-primary)' },
              ...disabledBtnSx,
            }}
          >
            Update Package
          </Button>
        )}
        {isOutstation && bookingOnlyNote}
      </Box>

      {/* Add New Package Modal */}
      <Dialog open={isAddPackageModalOpen} onClose={() => setIsAddPackageModalOpen(false)}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Add new package
          <IconButton onClick={() => setIsAddPackageModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Package name"
            value={newPackageName}
            onChange={(e) => setNewPackageName(e.target.value)}
            fullWidth
            margin="normal"
            required
            error={isAddAttempted && !newPackageName.trim()}
            helperText={isAddAttempted && !newPackageName.trim() ? 'Package name is required' : ''}
          />
          <TextField
            label="Description"
            value={newPackageDescription}
            onChange={(e) => setNewPackageDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            margin="normal"
          />
          <Button
            variant="contained"
            onClick={handleAddNewPackageName}
            sx={{
              mt: 2,
              backgroundColor: 'var(--brand-primary)',
              borderRadius: '8px',
              paddingY: '8px',
              paddingX: '24px',
              textTransform: 'none',
              fontWeight: 500,
              color: 'white',
              '&:hover': { backgroundColor: 'var(--brand-primary)' },
            }}
          >
            Add
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReservationRateCardForm;
